"use client"

import * as React from "react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { StatusBadge } from "@exxatdesignux/ui/components/status-badge"
import { initialsFromDisplayName } from "@/lib/initials-from-name"
import { relativeTime } from "@/lib/relative-time"
import type { QuestionPreview, QuestionVersion } from "@/lib/question-preview"
import { cn } from "@/lib/utils"

export function currentQuestionVersionNumber(
  versions: QuestionVersion[],
): number {
  return (
    versions.find((version) => version.current)?.number ??
    versions[0]?.number ??
    1
  )
}

export function questionVersionByNumber(
  versions: QuestionVersion[],
  number: number,
): QuestionVersion | undefined {
  return versions.find((version) => version.number === number) ?? versions[0]
}

function PersonLine({
  name,
  email,
  className,
}: {
  name: string | undefined
  email: string | undefined
  className?: string
}) {
  if (!name) return null
  return (
    <span className={cn("flex min-w-0 items-center gap-2.5 text-left", className)}>
      <Avatar shape="circle" className="shrink-0">
        <AvatarFallback className="text-xs font-semibold">
          {initialsFromDisplayName(name).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium leading-5 text-foreground">
          {name}
        </span>
        {email ? (
          <span className="truncate text-sm leading-5 text-muted-foreground">
            {email}
          </span>
        ) : null}
      </span>
    </span>
  )
}

export function CreatorHistoryCard({
  author,
  authorEmail,
  updatedOn,
  versions,
  selected,
  onOpenHistory,
  title = "Creator and history",
  className,
  coachMarkId,
}: {
  author?: string
  authorEmail?: string
  updatedOn: string
  versions: QuestionVersion[]
  selected: QuestionVersion
  onOpenHistory?: () => void
  title?: string
  className?: string
  coachMarkId?: string
}) {
  const hasHistory = versions.length > 1
  const canOpenHistory = Boolean(onOpenHistory)
  const updated = relativeTime(selected.date || updatedOn)
  const dateline = (
    <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs leading-4 text-muted-foreground">
      <span className="font-medium tabular-nums text-foreground">
        v{selected.number}
      </span>
      <span aria-hidden>·</span>
      <span>{updated?.compact ?? "Unknown"}</span>
    </span>
  )

  const body = (
    <>
      {author ? (
        <PersonLine
          name={author}
          email={authorEmail}
          className="min-w-0 flex-1"
        />
      ) : (
        <span className="min-w-0 flex-1" />
      )}
      <span className="flex shrink-0 items-center gap-2">
        <span className="flex flex-col items-end gap-1">
          {dateline}
          {selected.current ? null : (
            <StatusBadge
              label="Not current"
              tone="warning"
              icon="fa-clock-rotate-left"
            />
          )}
        </span>
        {canOpenHistory ? (
          <i
            className="fa-light fa-chevron-right text-xs text-muted-foreground"
            aria-hidden
          />
        ) : null}
      </span>
    </>
  )

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {canOpenHistory ? (
        <button
          type="button"
          onClick={onOpenHistory}
          data-coach-mark={coachMarkId}
          aria-label={`Version history, ${versions.length} ${versions.length === 1 ? "version" : "versions"}. Showing version ${selected.number}, updated ${updated?.absoluteDate ?? "on an unknown date"}.`}
          className="flex w-full items-center gap-3 rounded-2 border border-border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {body}
        </button>
      ) : (
        <div className="flex w-full items-center gap-3 rounded-2 border border-border p-3">
          {body}
        </div>
      )}
    </div>
  )
}

export function QuestionVersionTimeline({
  versions,
  selectedNumber,
  onSelect,
  header,
}: {
  versions: QuestionVersion[]
  selectedNumber: number
  onSelect: (number: number) => void
  header?: React.ReactNode
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {header}
      <ol className="mt-1 flex flex-col">
        {versions.map((version, index) => {
          const isSelected = version.number === selectedNumber
          const isLast = index === versions.length - 1
          const when = relativeTime(version.date)
          return (
            <li key={version.number} className="flex gap-3">
              <div
                className="flex w-2 shrink-0 flex-col items-center pt-2.5"
                aria-hidden
              >
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    version.current ? "bg-brand-1" : "bg-border",
                  )}
                />
                {isLast ? null : (
                  <span className="mt-1 w-px flex-1 bg-border" />
                )}
              </div>
              <button
                type="button"
                onClick={() => onSelect(version.number)}
                aria-current={isSelected ? "true" : undefined}
                aria-label={[
                  `Version ${version.number}`,
                  version.current ? "current" : null,
                  when?.absoluteDate,
                  version.note,
                  version.author,
                ]
                  .filter(Boolean)
                  .join(". ")}
                className={cn(
                  "mb-1 min-w-0 flex-1 rounded-2 px-2.5 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isSelected ? "bg-brand-1/8" : "hover:bg-muted/60",
                )}
              >
                <span
                  aria-hidden
                  className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs leading-4 text-muted-foreground"
                >
                  <span className="font-medium tabular-nums text-foreground">
                    v{version.number}
                  </span>
                  <span>·</span>
                  <span>{when?.compact ?? "Unknown"}</span>
                  {version.current ? (
                    <>
                      <span>·</span>
                      <span>Current</span>
                    </>
                  ) : null}
                </span>
                <span
                  aria-hidden
                  className="mt-1 block text-sm leading-5 text-foreground"
                >
                  {version.note}
                </span>
                {version.author ? (
                  <span
                    aria-hidden
                    className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs leading-4 text-muted-foreground"
                  >
                    <Avatar shape="circle" size="sm" className="shrink-0">
                      <AvatarFallback className="text-xs font-semibold">
                        {initialsFromDisplayName(version.author).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate">
                      {version.onBehalfOf ? `${version.author} for ${version.onBehalfOf}` : version.author}
                    </span>
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export function versionHistoryHeader(
  preview: Pick<QuestionPreview, "updatedOn" | "versions">,
  itemLabel = "Revised item",
): React.ReactNode {
  const count = preview.versions.length
  return (
    <div className="flex shrink-0 flex-col gap-0.5">
      <p className="text-xs leading-4 text-muted-foreground">
        {itemLabel} · {count} version{count === 1 ? "" : "s"}
      </p>
      <h2 className="m-0 text-sm font-semibold text-foreground">
        Version history
      </h2>
    </div>
  )
}
